import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { api } from '../lib/api';
import type { Client } from '@shared/models';

const INDUSTRIES = [
  '建設業',
  '土木',
  '建築',
  '住宅',
  '設備工事',
  '電気工事',
  'リフォーム',
  '不動産',
  '製造業',
  '運送業',
  'その他',
];

interface FormState {
  name: string;
  industry: string;
  location: string;
  employees: string;
  notes: string;
}

const empty: FormState = { name: '', industry: '建設業', location: '', employees: '', notes: '' };

export default function ClientForm({ mode }: { mode: 'create' | 'edit' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(empty);
  const [loading, setLoading] = useState(mode === 'edit');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== 'edit' || !id) return;
    api
      .get<Client>(`/api/clients/${id}`)
      .then((c) => {
        setForm({
          name: c.name,
          industry: c.industry || '',
          location: c.location || '',
          employees: c.employees != null ? String(c.employees) : '',
          notes: c.notes || '',
        });
      })
      .catch((e) => setError(e instanceof Error ? e.message : '読み込みに失敗しました'))
      .finally(() => setLoading(false));
  }, [mode, id]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const payload = {
      name: form.name.trim(),
      industry: form.industry.trim() || null,
      location: form.location.trim() || null,
      employees: form.employees ? Number(form.employees) : null,
      notes: form.notes.trim() || null,
    };
    try {
      if (mode === 'create') {
        await api.post('/api/clients', payload);
      } else {
        await api.put(`/api/clients/${id}`, payload);
      }
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="mx-auto max-w-2xl px-4 py-8 text-slate-400">読み込み中...</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-2xl px-4 py-8">
        <h1 className="mb-6 text-2xl font-bold text-slate-800">
          {mode === 'create' ? '新規クライアント追加' : 'クライアント編集'}
        </h1>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-rank-1">{error}</div>
        )}

        <form onSubmit={onSubmit} className="card space-y-4">
          <div>
            <label className="label">会社名 <span className="text-rank-1">*</span></label>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input"
            />
          </div>

          <div>
            <label className="label">業種</label>
            <select
              value={form.industry}
              onChange={(e) => setForm({ ...form, industry: e.target.value })}
              className="input"
            >
              <option value="">選択しない</option>
              {INDUSTRIES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">所在地</label>
            <input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="例: 東京都新宿区"
              className="input"
            />
          </div>

          <div>
            <label className="label">従業員数</label>
            <input
              type="number"
              min={0}
              value={form.employees}
              onChange={(e) => setForm({ ...form, employees: e.target.value })}
              className="input"
            />
          </div>

          <div>
            <label className="label">メモ</label>
            <textarea
              rows={4}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="input"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? '保存中...' : '保存'}
            </button>
            <Link to="/" className="btn-secondary">
              キャンセル
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}
